import React from "react";
import { SectionHeader } from "@/components/design-system/SectionHeader/SectionHeader";

type SolutionCard = {
  readonly image: string;
  readonly alt: string;
  readonly width: string;
};

const solutionCards: readonly SolutionCard[] = [
  { image: "/card-wealth.png", alt: "Wealth", width: "w-28.75 md:w-24.5" },
  { image: "/card-fintech.png", alt: "Fintech", width: "w-37" },
  { image: "/card-credit-union.png", alt: "Credit Union", width: "w-45" },
  { image: "/card-eap.png", alt: "EAP", width: "w-37 md:w-41" },
  {
    image: "/card-employer.png",
    alt: "Employer",
    width: "w-32 md:w-33",
  },
  {
    image: "/card-advisory.png",
    alt: "Advisory",
    width: "w-28 md:w-24.5",
  },
  { image: "/card-bank.png", alt: "Bank", width: "w-28 md:w-24.5" },
] as const;

export const EnterpriseSolutionsSection: React.FC = () => {
  return (
    <section id="solutions" className="relative w-full bg-white">
      <div className="mx-auto max-w-360">
        {/* Mobile Layout */}
        <div className="relative z-10 flex flex-col gap-6 pt-12 pb-10 md:hidden">
          <div className="flex flex-col gap-4 text-center">
            <SectionHeader
              label="Enterprise Solutions"
              heading={<>Built for every{" "}<span className="text-primary-500">financial experience.</span></>}
              description="Whether you're a large financial institution or a niche advisory firm, Bravo's intelligence layer adapts to your audience while helping you achieve your goals."
            />
          </div>

          <div className="relative overflow-hidden">
            {/* Mobile Gradient */}
            <svg
              className="pointer-events-none absolute top-1/2 left-1/2 h-full w-auto -translate-x-1/2 -translate-y-1/2"
              viewBox="0 0 659 659"
              fill="none"
            >
              <circle
                cx="329.5"
                cy="329.5"
                r="329.5"
                fill="url(#ellipse-mobile)"
                fillOpacity="0.7"
              />
              <defs>
                <radialGradient
                  id="ellipse-mobile"
                  cx="0"
                  cy="0"
                  r="1"
                  gradientUnits="userSpaceOnUse"
                  gradientTransform="translate(329.5 329.5) rotate(120.385) scale(381.963)"
                >
                  <stop stopColor="#D441D6" />
                  <stop offset="0.427885" stopColor="#E4A96B" />
                  <stop
                    offset="0.657599"
                    stopColor="#FEB7FF"
                    stopOpacity="0.42029"
                  />
                  <stop offset="0.831731" stopColor="white" stopOpacity="0" />
                </radialGradient>
              </defs>
            </svg>

            <div className="relative z-10 w-full px-4 py-8">
              <div className="mx-auto flex max-w-sm flex-col">
                {/* Row 1: Wealth + Fintech */}
                <div className="flex items-end justify-center">
                  <img
                    src={solutionCards[0].image}
                    alt={solutionCards[0].alt}
                    loading="lazy"
                    className={`${solutionCards[0].width} h-auto`}
                  />
                  <img
                    src={solutionCards[1].image}
                    alt={solutionCards[1].alt}
                    loading="lazy"
                    className={`${solutionCards[1].width} h-auto`}
                  />
                </div>

                {/* Row 2: 2 columns */}
                <div className="flex">
                  {/* Col 1: Credit Union + EAP */}
                  <div className="flex flex-1 flex-col items-end">
                    <img
                      src={solutionCards[2].image}
                      alt={solutionCards[2].alt}
                      loading="lazy"
                      className={`${solutionCards[2].width} h-auto`}
                    />
                    <img
                      src={solutionCards[3].image}
                      alt={solutionCards[3].alt}
                      loading="lazy"
                      className={`${solutionCards[3].width} h-auto`}
                    />
                  </div>

                  {/* Col 2: Employer + Advisory + Bank */}
                  <div className="flex flex-1 flex-col items-start">
                    <img
                      src={solutionCards[4].image}
                      alt={solutionCards[4].alt}
                      loading="lazy"
                      className={`${solutionCards[4].width} h-auto`}
                    />
                    <img
                      src={solutionCards[5].image}
                      alt={solutionCards[5].alt}
                      loading="lazy"
                      className={`${solutionCards[5].width} h-auto`}
                    />
                    <img
                      src={solutionCards[6].image}
                      alt={solutionCards[6].alt}
                      loading="lazy"
                      className={`${solutionCards[6].width} h-auto`}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Desktop Layout */}
        <div className="relative z-10 hidden items-center gap-8 px-12 pb-16 md:flex xl:h-177.5 xl:gap-43 xl:px-46.25 xl:py-0">
          {/* Card Group */}
          <div className="relative z-10 min-w-0 flex-1 overflow-visible xl:h-113.5 xl:w-117.75 xl:flex-none">
            <svg
              className="pointer-events-none absolute top-1/2 left-1/2 aspect-square w-[150%] -translate-x-1/2 -translate-y-1/2"
              viewBox="0 0 709 709"
              fill="none"
            >
              <circle
                cx="354.5"
                cy="354.5"
                r="354.5"
                fill="url(#ellipse-desktop)"
                fillOpacity="0.7"
              />
              <defs>
                <radialGradient
                  id="ellipse-desktop"
                  cx="0"
                  cy="0"
                  r="1"
                  gradientUnits="userSpaceOnUse"
                  gradientTransform="translate(354.5 354.5) rotate(120.385) scale(411)"
                >
                  <stop stopColor="#D441D6" />
                  <stop offset="0.427885" stopColor="#E4A96B" />
                  <stop
                    offset="0.657599"
                    stopColor="#FEB7FF"
                    stopOpacity="0.42029"
                  />
                  <stop offset="0.831731" stopColor="white" stopOpacity="0" />
                </radialGradient>
              </defs>
            </svg>

            <div className="relative z-10 flex w-full items-center justify-center p-4 xl:h-113.5 xl:w-117.75">
              <div className="flex items-stretch">
                {/* Left column */}
                <div className="flex flex-col">
                  {/* Row 1: Wealth + Fintech */}
                  <div className="flex items-end justify-end">
                    <img
                      src={solutionCards[0].image}
                      alt={solutionCards[0].alt}
                      loading="lazy"
                      className={`${solutionCards[0].width} h-auto`}
                    />
                    <img
                      src={solutionCards[1].image}
                      alt={solutionCards[1].alt}
                      loading="lazy"
                      className={`${solutionCards[1].width} h-auto`}
                    />
                  </div>

                  {/* Row 2: Credit Union | Advisory + Bank */}
                  <div className="flex">
                    <div className="flex items-start justify-end">
                      <img
                        src={solutionCards[2].image}
                        alt={solutionCards[2].alt}
                        loading="lazy"
                        className={`${solutionCards[2].width} h-auto`}
                      />
                    </div>

                    <div className="flex flex-col">
                      <img
                        src={solutionCards[5].image}
                        alt={solutionCards[5].alt}
                        loading="lazy"
                        className={`${solutionCards[5].width} h-auto`}
                      />
                      <img
                        src={solutionCards[6].image}
                        alt={solutionCards[6].alt}
                        loading="lazy"
                        className={`${solutionCards[6].width} h-auto`}
                      />
                    </div>
                  </div>
                </div>

                {/* Right column: EAP + Employer */}
                <div className="flex flex-col items-start justify-center">
                  <img
                    src={solutionCards[3].image}
                    alt={solutionCards[3].alt}
                    loading="lazy"
                    className={`${solutionCards[3].width} h-auto`}
                  />
                  <img
                    src={solutionCards[4].image}
                    alt={solutionCards[4].alt}
                    loading="lazy"
                    className={`${solutionCards[4].width} h-auto`}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Text */}
          <div className="relative z-10 flex min-w-0 flex-1 flex-col gap-6 xl:w-101 xl:flex-none">
            <SectionHeader
              align="left"
              label="Enterprise Solutions"
              heading={<>Built for every{" "}<span className="text-primary-500">financial experience.</span></>}
              description="Whether you're a large financial institution or a niche advisory firm, Bravo's intelligence layer adapts to your audience while helping you achieve your goals."
            />
          </div>
        </div>
      </div>
    </section>
  );
};
